// src/App.js
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import HeroSection from "./components/HeroSection";
import JournalStore from "./components/JournalStore";
import FeelingForm from "./components/FeelingForm";
import FeelingFeed from "./components/FeelingFeed";
import Footer from "./components/Footer";
import "./App.css";

// Real PDF viewer for /print/:slug
import JournalViewer from "./pages/JournalViewer";

// 🔔 Push helpers & Firestore
import { initPushForThisDevice, onForegroundMessage } from "./notifications";
import { db } from "./firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

// Lazy pages
const ThankYou   = lazy(() => import("./ThankYou"));
const StorageTest = lazy(() => import("./pages/StorageTest"));   // diagnostic route
const Settings   = lazy(() => import("./pages/Settings"));        // notification settings
const Privacy    = lazy(() => import("./pages/Privacy"));         // NEW
const Terms      = lazy(() => import("./pages/Terms"));           // NEW

// Hide DebugPush in production (no import cost, no route then)
const DebugPush =
  process.env.NODE_ENV !== "production"
    ? lazy(() => import("./pages/DebugPush"))
    : (() => null);

// -----------------------------------------------------------------------------
// Gentle, dismissible prompt to enable notifications.
// - Won't show if already granted or we've set ww_push_enabled=1
// - Won't show more than once per day if dismissed (ww_push_seen=YYYY-MM-DD)
// -----------------------------------------------------------------------------
function NotificationPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok:boolean, reason?:string, token?:string}

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;

    const perm = Notification.permission; // "default" | "granted" | "denied"
    const enabled = localStorage.getItem("ww_push_enabled") === "1";

    // hide if already granted or we marked as enabled once
    if (perm === "granted" || enabled) return;

    // throttle: once per day if dismissed
    const today = new Date().toISOString().slice(0, 10);
    const lastSeen = localStorage.getItem("ww_push_seen");
    if (lastSeen === today) return;

    if (perm === "default") setVisible(true);
  }, []);

  const markSeenToday = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem("ww_push_seen", today);
    } catch {}
  };

  const handleEnable = async () => {
    try {
      setBusy(true);
      const res = await initPushForThisDevice("anon");
      setResult(res);
      setBusy(false);

      if (res?.ok && res.token) {
        // save in Firestore (defensive; initPush already writes too)
        try {
          await setDoc(
            doc(db, "devices", res.token),
            {
              token: res.token,
              user: "anon",
              platform: "web-twa",
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        } catch (e) {
          console.warn("Failed to save device token (non-fatal):", e);
        }

        try {
          localStorage.setItem("ww_push_enabled", "1");
        } catch {}

        setVisible(false);
      } else {
        markSeenToday();
      }
    } catch (e) {
      setBusy(false);
      setResult({ ok: false, reason: e?.message || "unknown_error" });
      markSeenToday();
    }
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #e6d6ff",
        borderRadius: 12,
        boxShadow: "0 10px 30px rgba(106,13,173,0.15)",
        padding: "14px 16px",
        display: "flex",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: "#6a0dad" }}>
          Stay gently in touch 💜
        </div>
        <div style={{ fontSize: 13, color: "#555", marginTop: 2 }}>
          Get a daily calming quote and helpful journal nudges.
        </div>
        {result && !result.ok && (
          <div style={{ fontSize: 12, color: "#a00", marginTop: 6 }}>
            {result.reason === "denied"
              ? "You blocked notifications. You can enable them later from your browser settings."
              : "Couldn’t enable notifications right now."}
          </div>
        )}
      </div>

      <button
        onClick={() => {
          markSeenToday();
          setVisible(false);
        }}
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #ddd",
          background: "#fff",
          cursor: "pointer",
        }}
      >
        Not now
      </button>

      <button
        onClick={handleEnable}
        disabled={busy}
        style={{
          padding: "10px 14px",
          borderRadius: 10,
          border: "none",
          background: busy ? "#b08cd6" : "#6a0dad",
          color: "#fff",
          fontWeight: 700,
          cursor: busy ? "default" : "pointer",
        }}
      >
        {busy ? "Enabling…" : "Enable"}
      </button>
    </div>
  );
}

function Home() {
  return (
    <div className="App">
      <HeroSection />

      <section
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "32px 16px 40px",
          background: "#f8f1ff",
        }}
      >
        <div style={{ width: "100%", maxWidth: 1050 }}>
          <blockquote
            style={{
              textAlign: "center",
              fontStyle: "italic",
              margin: "0 auto 12px",
              color: "#555",
              maxWidth: 820,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            🌿 “Sometimes just expressing what you feel can be the first step to
            healing.”
          </blockquote>

          <h2
            style={{
              textAlign: "center",
              fontWeight: "800",
              color: "#6a0dad",
              marginBottom: 24,
              fontSize: 28,
            }}
          >
            💭 What are people feeling today?
          </h2>

          <FeelingForm />
          <div style={{ marginBottom: 0 }}>
            <FeelingFeed />
          </div>
        </div>
      </section>

      <div id="journals-section">
        <JournalStore />
      </div>

      <Footer />
    </div>
  );
}

export default function App() {
  // App-wide foreground message handler
  useEffect(() => {
    let unsubscribe = () => {};
    (async () => {
      unsubscribe = await onForegroundMessage((payload) => {
        try {
          const title =
            payload?.notification?.title || "WhisperWall notification";
          const body = payload?.notification?.body || "";
          if (typeof window !== "undefined" && "Notification" in window) {
            if (Notification.permission === "granted") {
              new Notification(title, { body });
            }
          }
        } catch {
          /* no-op */
        }
      });
    })();
    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch {
        /* ignore */
      }
    };
  }, []);

  return (
    <BrowserRouter>
      {/* Notification prompt shows only when needed */}
      <NotificationPrompt />

      <Suspense
        fallback={
          <div style={{ maxWidth: 720, margin: "64px auto", padding: 24 }}>
            <h1>Loading…</h1>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/print/:slug" element={<JournalViewer />} />

          {/* Settings page for notification subscribe/unsubscribe */}
          <Route path="/settings" element={<Settings />} />

          {/* Legal pages */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Diagnostic routes (hidden in production) */}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/dev/storage-test" element={<StorageTest />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/debug-push" element={<DebugPush />} />
          )}

          {/* Fallback */}
          <Route path="*" element={<Home />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
